// components
import { Button, Typography } from '@mui/material';
import { CalendarEventRouter } from './components/CalendarAppointment';
import { MemoCalendar } from './Calendar';
import TimeSlotWrapper from './components/TimeSlotWrapper/TimeSlotWrapper';
import EventWrapper from './components/EventWrapper/EventWrapper';

// hooks
import {
  ComponentType,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCalendarEvents,
  useEntityCalendarHooks
} from './EntityCalendar.utils';

// styles
import { CalendarRetryButtonContainer } from './Calendar.styles';

// types
import { EventWrapperProps, SlotInfo } from 'react-big-calendar';
import type {
  CalendarAppointmentEvent,
  // CalendarAvailabilityEvent,
  // ColumnWidth,
  EntityCalendarProps
} from './Calendar.types';
import { EventResponse, PlanningEntityIdentifier } from 'generated/index';
import { AppointmentPreviewData } from 'components/mrp/stores/appointmentStore/appointmentStore.types';
import { TimeSlotWrapperInjectedProps } from './components/TimeSlotWrapper/TimeSlotWrapper.types';

// utils
import {
  // calculateOverlapDepth,
  createEventResponseFromSlotInfo
} from './Calendar.utils';
import { createAppointmentFormDataFromEventResponse } from './components/CalendarAppointment/CalenderAppointment.utils';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';
import classNames from 'classnames';
// import { getUniqueEntities } from 'components/mrp/mrp.utils';

// consts
import {
  DAY_AVAILABILITIES,
  DAY_EVENTS,
  IS_AVAILABILITY_BACKGROUND_PREFIX,
  IS_ORIGINAL_PLACEHOLDER_ID,
  IS_PREVIEW_ID
} from '../AppointmentForm/AppointmentForm.consts';
import EntityCalendarPositionTracker from './components/EntityCalendarPositionTracker/EntityCalendarPositionTracker';
import { useSelector } from 'react-redux';

const EntityCalendar = memo(
  ({
    calendarId,
    planningEntityIdentifiers
  }: EntityCalendarProps<CalendarAppointmentEvent>) => {
    const { t } = useTranslation('common', { keyPrefix: 'mrp' });
    const { t: tCommon } = useTranslation('common');
    const isMounted = useRef(false);
    const hasAddAppointmentAccess = useSelector(
      ({ grants }) => grants.hasAddAppointmentAccess
    );

    const {
      calendar,
      timeSlotMax,
      timeSlotMin,
      timeStep,
      endDate,
      periodType,
      startDate,
      appointment,
      original,
      previewData,
      events,
      availabilities,
      isError,
      viewState,
      refetch,
      setSelectedDate,
      setEvent,
      setEvents,
      getPreviewData,
      setAppointmentData,
      setPosition
    } = useEntityCalendarHooks(calendarId, planningEntityIdentifiers);

    const [
      currentPlanningEntityIdentifiers,
      setCurrentPlanningEntityIdentifiers
    ] = useState<PlanningEntityIdentifier[]>(planningEntityIdentifiers);
    useEffect(() => {
      if (
        !isMounted.current ||
        isEqual(currentPlanningEntityIdentifiers, planningEntityIdentifiers)
      )
        return;

      setCurrentPlanningEntityIdentifiers(planningEntityIdentifiers);
      refetch();
    }, [currentPlanningEntityIdentifiers, planningEntityIdentifiers, refetch]);

    const calendarRef = useRef<HTMLDivElement | null>(null);

    const [previewEvents, setPreviewEvents] = useState<
      AppointmentPreviewData[]
    >([]);

    const calendarEvents: CalendarAppointmentEvent[] = useMemo(
      () =>
        getCalendarEvents({
          calendarId,
          events,
          original,
          previewEvents
        }),
      [calendarId, events, original, previewEvents]
    );

    useEffect(() => {
      setEvents(
        calendarId,
        calendarEvents.filter(({ resourceId }) => resourceId === DAY_EVENTS)
      );
    }, [calendarEvents, calendarId, setEvents]);

    // const uniqueEntities = useMemo(() => {
    //   return getUniqueEntities(calendar.sources);
    // }, [calendar]);

    // TODO: 1275 Remove after decision is made that we don't use colored borders in AppointmentPlanning.
    // These strip events render colored bars in the DAY_AVAILABILITIES column (left gutter).
    // Currently disabled: not included in allEvents, and column widths are passed as [].
    // const stripEvents: CalendarAvailabilityEvent[] = useMemo(() => {
    //   if (uniqueEntities.length > 1) return [];

    //   return availabilities.map((availability) => ({
    //     ...availability,
    //     resourceId: DAY_AVAILABILITIES
    //   }));
    // }, [availabilities, uniqueEntities]);

    // TODO: 1275 Remove after decision is made that we don't use colored borders in AppointmentPlanning.
    // const availabilityColumnWidths: ColumnWidth[] = useMemo(() => {
    //   const start = dayjs(startDate).startOf('day');
    //   const end = dayjs(endDate).startOf('day');
    //   const periodSize = end.diff(start, 'days');

    //   return Array.from({ length: periodSize + 1 }, (_, idx) => ({
    //     idx: idx + 1,
    //     columnWidth: calculateOverlapDepth(
    //       stripEvents.filter((av) =>
    //         dayjs(av.start).startOf('day').isSame(start.add(idx, 'days'))
    //       )
    //     )
    //   }));
    // }, [stripEvents]);

    const availabilityBackgroundEvents: CalendarAppointmentEvent[] = useMemo(
      () =>
        availabilities.map((av, idx) => ({
          id: `${IS_AVAILABILITY_BACKGROUND_PREFIX}${av.type}_${idx}`,
          availabilityType: av.type,
          start: av.start,
          end: av.end,
          title:
            av.label || (av.type === 'NOT_AVAILABLE' ? tCommon('absent') : ''),
          calendarId: av.calendarId,
          resourceId: DAY_EVENTS,
          planningEvent: {} as EventResponse
        })),
      [availabilities, tCommon]
    );

    const backgroundEvents: CalendarAppointmentEvent[] = useMemo(() => {
      const calendarEntityIds = planningEntityIdentifiers.map(
        ({ entityId }) => entityId!
      );

      const hasMatchingEntity = original?.entityIds.some((entityId) =>
        calendarEntityIds.includes(entityId)
      );

      const originalEvents =
        original && hasMatchingEntity
          ? [
              {
                id: IS_ORIGINAL_PLACEHOLDER_ID,
                start: new Date(original.start),
                end: new Date(original.end),
                title: '',
                calendarId: '',
                planningEvent: {} as EventResponse
              }
            ]
          : [];

      return [...originalEvents, ...availabilityBackgroundEvents];
    }, [availabilityBackgroundEvents, planningEntityIdentifiers, original]);

    const handleSelectSlot = (slotInfo: SlotInfo) => {
      if (!hasAddAppointmentAccess) return;

      if (slotInfo.action === 'click') {
        setPosition({
          calendarId,
          date: dayjs(slotInfo.start)
        });
        return;
      }

      const eventResponse = createEventResponseFromSlotInfo(
        slotInfo,
        calendar.sources
      ) as EventResponse;

      setAppointmentData(
        createAppointmentFormDataFromEventResponse(eventResponse)!
      );
    };

    const isNonInteractiveEvent = (event: CalendarAppointmentEvent) =>
      event.id === IS_PREVIEW_ID ||
      event.id === IS_ORIGINAL_PLACEHOLDER_ID ||
      !!event.availabilityType;

    const handleClick = (event: CalendarAppointmentEvent) => {
      if (isNonInteractiveEvent(event) || appointment) return;

      setEvent(event);
      setPosition({ calendarId, date: dayjs(event.start) });
    };

    const handleDoubleClick = (
      event: CalendarAppointmentEvent,
      e: React.SyntheticEvent<HTMLElement>
    ) => {
      if (isNonInteractiveEvent(event) || !event.planningEvent?.isEditable)
        return;

      e.preventDefault();
      e.stopPropagation();

      setAppointmentData(
        createAppointmentFormDataFromEventResponse(event.planningEvent)!
      );
    };

    useEffect(() => {
      const previews = getPreviewData(
        planningEntityIdentifiers.map(({ entityId }) => entityId!),
        startDate!,
        endDate!
      );

      setPreviewEvents((current) =>
        isEqual(current, previews) ? current : previews
      );
    }, [
      endDate,
      getPreviewData,
      planningEntityIdentifiers,
      previewData,
      startDate
    ]);

    const [allEvents, setAllEvents] = useState<CalendarAppointmentEvent[]>([]);
    useEffect(() => {
      // TODO: 1275 Remove after decision is made that we don't use colored borders in AppointmentPlanning.
      // To re-enable: [...calendarEvents, ...(stripEvents as unknown as CalendarAppointmentEvent[])]
      const newEventSet = [...calendarEvents];

      if (isEqual(newEventSet, allEvents)) return;

      setAllEvents(newEventSet);
    }, [allEvents, calendarEvents]);

    // This must be the final useEffect to ensure all other useEffects
    // are triggered first on the initial pass.
    useEffect(() => {
      if (!isMounted.current) isMounted.current = true;
    }, [isMounted]);

    const eventPropGetter = (event: CalendarAppointmentEvent) => {
      if (event.availabilityType === 'NOT_AVAILABLE') {
        return {
          className: 'not-available-bg-event'
        };
      }

      return {};
    };

    if (isError) {
      return (
        <CalendarRetryButtonContainer>
          <Typography variant="body1" color="error">
            {t('error.calendarError')}
          </Typography>

          <Button onClick={() => refetch()}>{t('tryAgain')}</Button>
        </CalendarRetryButtonContainer>
      );
    }

    return (
      <EntityCalendarPositionTracker calendarId={calendarId}>
        <MemoCalendar<CalendarAppointmentEvent>
          resources={[
            {
              resourceId: DAY_AVAILABILITIES,
              resourceTitle: ''
            },
            {
              resourceId: DAY_EVENTS,
              resourceTitle: ''
            }
          ]}
          className={classNames(
            `view-state-${viewState.toLocaleLowerCase()}`,
            calendar.sources[0]?.planningEntityIdentifier.planningEntityType &&
              `entity-type-${calendar.sources[0].planningEntityIdentifier.planningEntityType.toLowerCase()}`
          )}
          // TODO: 1275 Remove after decision is made that we don't use colored borders in AppointmentPlanning.
          // Pass [] instead of availabilityColumnWidths to hide the strip column.
          availabilityColumnWidths={[]}
          components={{
            event: CalendarEventRouter,
            // Cast is required because RBC passes certain properties into its components
            // that we need acces to, but RBC's internal typings dont contain these properties.
            timeSlotWrapper: (({
              children,
              value,
              resource
            }: TimeSlotWrapperInjectedProps) => (
              <TimeSlotWrapper
                value={value}
                resource={resource}
                calendarId={calendarId}
              >
                {children}
              </TimeSlotWrapper>
            )) as ComponentType<{}>,
            eventWrapper: EventWrapper as ComponentType<
              EventWrapperProps<CalendarAppointmentEvent>
            >
          }}
          events={allEvents}
          backgroundEvents={backgroundEvents}
          eventPropGetter={eventPropGetter}
          endDate={endDate || new Date()}
          onDoubleClick={handleDoubleClick}
          onNavigate={setSelectedDate}
          onSelectEvent={handleClick}
          onSelectSlot={handleSelectSlot}
          periodType={periodType}
          ref={calendarRef}
          selectable={!appointment && hasAddAppointmentAccess}
          startDate={startDate || new Date()}
          timeSlotMinMax={[timeSlotMin, timeSlotMax]}
          timeStep={timeStep}
        />
      </EntityCalendarPositionTracker>
    );
  },
  (prev, next) => {
    return (
      prev.calendarId === next.calendarId &&
      isEqual(prev.planningEntityIdentifiers, next.planningEntityIdentifiers)
    );
  }
);

export default EntityCalendar;
